import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Dropdown, Input, Modal, Tooltip } from "antd";
import {
  Search,
  MoreHorizontal,
  Bookmark,
  Tag,
  Workflow,
  UserPlus,
  Printer,
} from "lucide-react";
import { ReactComponent as ExcelIcon } from "../../assets/svg/file-excel-solid.svg";
import { ReactComponent as PdfIcon } from "../../assets/svg/file-pdf-solid.svg";

import "../../assets/css/dynemic-page.css";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import TrialModal from "../../common/TrialModal";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { errEnum, subscriptionPlans } from "../../config";
import {
  emptyErrorObj,
  saveSearchAction,
} from "../../actionCreator/companyListingActionCreater";
import { AuthContext } from "../../context/AuthContext";
import { getUserInfo } from "../../utils/utils";
import { useAlert } from "../../context/AlertProvider";
import { useExport } from "./hooks/useExport";
import TagExecutive from "./TagExecutive";
import { getTagList } from "../../actionCreator/dashboardActionCreator";
import BulkAddToLead from "../company-summary/BulkAddToLead";
import {
  getExecutiveEmployeeList,
  selectedRow,
} from "../../actionCreator/executiveListingActionCreater";
import useUpgradeAlert from "../../common/hooks/useUpgradeAlert";
import { useDebounce } from "../../common/hooks/useDebounce";

const ExecutiveHeaderFilter = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const showUpgradeAlert = useUpgradeAlert();
  const { onExport } = useExport();
  const { isAuthenticated: isLoggedIn, subscription } = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("searchKeyword") || "",
  );
  const page = searchParams.get("page");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [isAddToLead, setAddToLead] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const [searchValues, setSearchValues] = useState({
    tagname: "",
    description: "",
    tagError: "",
  });
  const errObj = useSelector((state) => state.executiveListingReducer.errObj);
  const selectedRecords = useSelector(
    (state) => state.executiveListingReducer.selectedRecords,
  );
  const hasOtherFilters = Array.from(searchParams.keys()).some(
    (key) => key !== "page" && key !== "size",
  );

  const onFilterChange = (key, val) => {
    if (val !== undefined) {
      searchParams.set(key, val);
    } else {
      searchParams.delete(key);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const onInputChange = (e) => {
    setSearchValues({
      ...searchValues,
      [e.target.name]: e.target.value,
    });
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
  };

  const closeTagModal = () => {
    setShowTagModal(false);
  };

  const onConfrim = async () => {
    if (!searchValues.tagname) {
      setSearchValues({
        ...searchValues,
        tagError: "error",
      });
    } else {
      const { id, email, login } = getUserInfo();
      const queryObject = Object.fromEntries(searchParams.entries());
      const { page, size, ...filteredQuery } = queryObject;
      const payload = {
        accountId: login,
        dataDump: JSON.stringify(filteredQuery),
        fullName: searchValues.tagname,
        emailId: email,
        source: "Executive",
        userId: id,
      };
      const res = await dispatch(saveSearchAction(payload));
      if (res?.status === 201) {
        showAlert("success", "Saved Search Created!");
        setSearchValues({
          tagname: "",
          description: "",
          tagError: "",
        });
        setShowModal(false);
      } else {
        showAlert("error", res?.response?.data?.title);
        setSearchValues({
          ...searchValues,
          tagError: res?.response?.data?.title,
        });
      }
    }
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  };

  const onHandleSaveSearch = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      setShowModal(true);
    }
  };

  const tagPage = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      if (selectedRecords.length) {
        setShowTagModal(true);
      } else {
        showAlert("warning", "please select record.");
      }
    }
  };

  const handleAddToLead = () => {
    if (selectedRecords?.length) {
      setAddToLead(true);
    } else {
      showAlert("warning", "please select record.");
    }
  };

  const items = [
    {
      key: "1",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={() => {
            checkLoginStatus();
            onExport(
              selectedRecords?.map((d) => d?.key),
              "exl",
            );
          }}
        >
          <ExcelIcon style={{ width: 16, height: 16, fill: "#217346" }} />
          Export into Excel
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={() => {
            checkLoginStatus();
            onExport(
              selectedRecords?.map((d) => d?.key),
              "pdf",
            );
          }}
        >
          <PdfIcon style={{ width: 16, height: 16, fill: "#e53935" }} />
          Export into PDF
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="tw-text-sm tw-flex tw-items-center tw-gap-2.5 tw-text-slate-700 tw-py-1 tw-px-1.5 tw-font-semibold tw-font-inter"
          onClick={() => {
            checkLoginStatus();
            if (isLoggedIn) {
              window.print();
            }
          }}
        >
          <Printer size={16} className="tw-text-slate-400" />
          Print this list
        </div>
      ),
    },
  ].filter(Boolean);

  useEffect(() => {
    if (debouncedSearchTerm !== null) {
      onFilterChange(
        "searchKeyword",
        debouncedSearchTerm.trim() !== "" ? debouncedSearchTerm : undefined,
      );
      onFilterChange("Saved Search", undefined);
      onFilterChange("page", page || 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  return (
    <div className="tw-flex tw-items-center tw-gap-[10px]">
      <BulkAddToLead
        selectedItems={selectedRecords?.map((d) => d?.executiveData)}
        isOpen={isAddToLead}
        handleCancel={() => setAddToLead(false)}
        callback={() => {
          const allParams = {};
          for (const [key, value] of searchParams.entries()) {
            allParams[key] = value;
          }

          dispatch(
            getExecutiveEmployeeList({
              ...allParams,
              page: allParams?.page ? allParams?.page - 1 : 0,
            }),
          );
          dispatch(selectedRow([]));
        }}
      />

      <Input
        value={searchTerm}
        placeholder="Search By Designation"
        allowClear
        suffix={
          <Search
            size={18}
            className="tw-cursor-pointer tw-text-slate-400 hover:tw-text-[#5D44FF]"
            onClick={() => {
              onFilterChange(
                "searchKeyword",
                searchTerm.trim() !== "" ? searchTerm : undefined,
              );
              onFilterChange("Saved Search", undefined);
              onFilterChange("page", page || 1);
            }}
          />
        }
        onPressEnter={() => {
          onFilterChange(
            "searchKeyword",
            searchTerm.trim() !== "" ? searchTerm : undefined,
          );
          onFilterChange("Saved Search", undefined);
          onFilterChange("page", page || 1);
        }}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="!tw-h-[38px] tw-w-[230px] !tw-rounded-lg !tw-border-slate-200"
      />
      <Tooltip title="Save Search">
        <div
          className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150"
          onClick={() => {
            if (
              [subscriptionPlans?.trail].includes(
                subscription?.subscriptionPackage?.name,
              )
            ) {
              showUpgradeAlert();
            } else {
              onHandleSaveSearch();
            }
          }}
          disabled={
            [subscriptionPlans?.trail].includes(
              subscription?.subscriptionPackage?.name,
            )
              ? false
              : !hasOtherFilters
          }
          title={!hasOtherFilters ? "Filters are not selected" : ""}
        >
          <Bookmark size={18} className="tw-text-slate-600" />
        </div>
      </Tooltip>

      <Tooltip title="Add Tags">
        <div
          className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150"
          onClick={() => {
            tagPage();
          }}
        >
          <Tag size={18} className="tw-text-slate-600" />
        </div>
      </Tooltip>
      <Tooltip title="Add to Lead">
        <div
          className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150"
          onClick={() => {
            if (isLoggedIn) {
              handleAddToLead();
            } else {
              checkLoginStatus();
            }
          }}
        >
          <UserPlus size={18} className="tw-text-slate-600 tw-cursor-pointer" />
        </div>
      </Tooltip>
      <FeatureComingSoon>
        <Tooltip title="Create Sequence">
          <div className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150">
            <Workflow size={18} className="tw-text-slate-600" />
          </div>
        </Tooltip>
      </FeatureComingSoon>
      <Dropdown menu={{ items }} trigger={["click"]}>
        <div className="tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-size-[38px] tw-border-solid tw-border-slate-200 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-all tw-duration-150">
          <MoreHorizontal size={20} className="tw-text-slate-600" />
        </div>
      </Dropdown>
      <TagExecutive
        selectedRecords={selectedRecords?.map((d) => d?.key)}
        showTagModal={showTagModal && isLoggedIn}
        closeTagModal={closeTagModal}
        callback={(userId) => dispatch(getTagList("EXECUTIVE"))}
      />

      {showModal ? (
        isLoggedIn ? (
          <Modal
            title={null}
            width={"400px"}
            className="delete-popup"
            closable={true}
            footer={null}
            open={showModal}
            onCancel={closeModal}
            onOk={onConfrim}
            centered
          >
            <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
              Save Search
            </div>
            <div className="pop-up errorformcontainer tw-p-6">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                  Search Name
                </span>

                <div>
                  <Input
                    name="tagname"
                    status={searchValues?.tagError}
                    value={searchValues.tagname}
                    placeholder="Name"
                    onChange={onInputChange}
                    className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200"
                  />
                </div>
              </div>
            </div>
            <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
              <Button
                className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
                onClick={() => {
                  closeModal();
                }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
                onClick={onConfrim}
              >
                Ok
              </Button>
            </div>
          </Modal>
        ) : (
          <TrialModal
            openModal={showModal}
            closeModal={closeModal}
            redirectToSignup={redirectToSignup}
            redirect={true}
          />
        )
      ) : (
        ""
      )}

      {errObj[errEnum.EXECUTIVE_DOWNLOAD_ERROR] && (
        <TrialModal
          openModal={!!errObj[errEnum.EXECUTIVE_DOWNLOAD_ERROR]}
          closeModal={() => dispatch(emptyErrorObj())}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {errObj[errEnum.EXECUTIVE_DOWNLOAD_ERROR]
                ? errObj[errEnum.EXECUTIVE_DOWNLOAD_ERROR].message
                : "Unable to complete the action. Please try again later."}
            </div>
          }
          modalWidth="400px"
        />
      )}

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          isSubscriptionMissing
        />
      )}
    </div>
  );
};

export default ExecutiveHeaderFilter;
